import { FormField, Input, Paragraph } from '@vwfs-bronson/bronson-react';
import { useField } from 'formik';
import React, { FormEventHandler, MouseEventHandler } from 'react';
import { InfoIcon } from '../../info-icon';
import { hasError, hasSuccess, getInputErrorMessageComponentAndId } from '../errors';

export type ValidatedInputProps = {
    children?: React.ReactNode;
    name: string;
    addonText?: string | React.ReactNode;
    reversed?: boolean;
    maxLength?: number;
    label: string;
    hint?: string | React.ReactNode;
    onLinkClick?: MouseEventHandler;
    type?: 'tel' | 'email' | 'password' | 'search' | 'text' | 'url'; // some theoretically possible HTML types have been remove from list, as they change the layout to a non brand compliant layout
    inputMode?: 'numeric' | 'text' | 'search' | 'tel' | 'url' | 'email' | 'decimal' | 'none';
    testId?: string;
    tooltip?: string | React.ReactNode;
    tooltipTitle?: string;
    id?: string;
    placeholder?: string;
    handleChange?: FormEventHandler<HTMLElement>;
    stateIcon?: boolean;
    isMandatory?: boolean;
    requiredText?: string;
    withHtmlValidationWarning?: boolean;
    onFocus?: () => void;
    disabled?: boolean;
    addonType?: string;
    addonIcon?: string;
    addonClassName?: string;
    modifierStates?: string;
    inputId?: string;
    className?: string;
    inputClassName?: string;
    ghost?: boolean;
    noBackground?: boolean;
    helpText?: string | React.ReactNode;
    helpTextClassName?: string;
    addonElement?: string;
    autocomplete?: string;
};

export const ValidatedInput: React.FC<ValidatedInputProps & React.InputHTMLAttributes<HTMLInputElement>> = ({
    children,
    name,
    label,
    hint,
    onLinkClick,
    testId,
    id,
    tooltip,
    tooltipTitle,
    handleChange,
    isMandatory = false,
    requiredText = '*',
    withHtmlValidationWarning = false,
    inputId,
    inputClassName,
    className,
    helpText,
    helpTextClassName,
    addonElement,
    autocomplete,
    disabled,
    ...inputProps
}) => {
    const [field, meta] = useField({ name, type: 'input' });
    const infoIcon = tooltip ? <InfoIcon text={tooltip} title={tooltipTitle} /> : null;

    const { errorMessageComponent, errorMessageId } = getInputErrorMessageComponentAndId({
        name,
        id,
        meta,
        testId,
        withHtmlValidationWarning,
    });

    const errorMessage = (
        <>
            {!disabled && errorMessageComponent}
            {!!helpText && !hasError(meta) && (
                <Paragraph
                    className={`u-text-muted u-text-fs-1 u-pt-xsmall ${helpTextClassName ?? ''}`}
                    testId={`${testId}HelpText`}
                >
                    {helpText}
                </Paragraph>
            )}
        </>
    );

    return (
        <FormField
            type="input"
            onClick={onLinkClick}
            hint={hint}
            labelText={label}
            labelForId={inputId}
            testId={`${testId}FormField`}
            id={id}
            errorMessage={errorMessage}
            infoIcon={infoIcon}
            onChange={handleChange}
            required={isMandatory}
            requiredText={isMandatory ? requiredText : undefined}
            className={className}
        >
            <Input
                error={!disabled && hasError(meta)}
                success={!disabled && hasSuccess(meta)}
                testId={testId}
                id={inputId}
                className={inputClassName}
                addonElement={addonElement}
                autoComplete={autocomplete}
                disabled={disabled}
                aria-invalid={hasError(meta)}
                aria-describedby={errorMessageId}
                required={isMandatory}
                {...inputProps}
                {...field}
            >
                {children}
            </Input>
        </FormField>
    );
};
